import { Link } from "react-router-dom";
import { ArrowRight, Leaf, Ruler, Sparkles } from "lucide-react";
import { Reveal, MaskedLine } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import useSEO from "@/hooks/useSEO";
import { img } from "@/lib/format";

export default function About() {
  useSEO({
    title: "Our Story — Just Kords | Co-ord Sets for Every Mood",
    description: "Just Kords makes stylish co-ord sets for women in small batches — luxe fabrics, flatter-first fits and considered production. Read the brand story behind justkords.com.",
  });

  return (
    <div className="bg-alabaster pt-28 lg:pt-36">
      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8 lg:pb-24">
        <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-crimson">Our Story</p>
        <h1 className="mt-6 max-w-4xl font-serif text-5xl font-light leading-[1.02] sm:text-7xl">
          <MaskedLine index={0}>Considered clothing for</MaskedLine>
          <MaskedLine index={1}>the woman who dresses</MaskedLine>
          <MaskedLine index={2}><em className="text-crimson">for herself.</em></MaskedLine>
        </h1>
      </section>

      <Marquee dark items={["Just Kords", "Co-ord sets for every mood", "Small batch", "Designed in India"]} />

      <section className="mx-auto grid max-w-7xl items-center gap-14 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:px-8 lg:py-24">
        <Reveal>
          <div className="relative">
            <img src={img("https://images.unsplash.com/photo-1604904612715-47bf9d9bc670", 1200)} alt="Just Kords tailoring detail" className="aspect-[4/5] w-full object-cover" />
            <div className="absolute -bottom-5 -right-5 hidden h-full w-full border-2 border-crimson lg:block" style={{ transform: "translate(20px, -20px)", zIndex: -1 }} />
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-serif text-4xl font-light leading-tight sm:text-5xl">It started with a simple frustration.</h2>
          <div className="mt-6 space-y-5 text-sm leading-relaxed text-mutedc sm:text-base">
            <p>
              Separates that don't match. Sets that look great on hangers and nothing like it on real bodies. Occasion-wear that demands dry cleaning after one wear. Just Kords began as the answer.
            </p>
            <p>
              We design co-ord sets — tops and bottoms made to be worn together and apart — in fabrics that breathe and cuts that flatter. A muslin kurta over denim. A crepe set under a blazer. Our sets are wardrobes, not outfits.
            </p>
            <p>
              Every drop is small-batch, fit-tested across sizes XS–XXL, and priced honestly. When a set sells out, it makes room for the next mood.
            </p>
          </div>
        </Reveal>
      </section>

      <section className="border-y border-linec bg-white py-16 lg:py-24">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 md:grid-cols-3 lg:px-8">
          {[
            [Sparkles, "Fabrics with a conscience", "Breathable muslin, fluid rayon, textured crepe and honest slub cotton — chosen for drape, comfort and longevity, never for a price point."],
            [Ruler, "Fits tested on real bodies", "Every pattern is worn, washed and re-fitted across sizes before production. If it doesn't flatter, it doesn't ship."],
            [Leaf, "Small-batch, low-waste", "Limited runs keep waste down and exclusivity up. Leftover fabric becomes accessories, never landfill."],
          ].map(([Icon, t, d], i) => (
            <Reveal key={t} delay={i * 0.1}>
              <div data-testid="about-value-card" className="flex h-full flex-col gap-4 border border-linec p-8 transition-colors duration-200 hover:border-crimson">
                <Icon size={26} className="text-crimson" strokeWidth={1.5} />
                <h3 className="font-serif text-2xl">{t}</h3>
                <p className="text-sm leading-relaxed text-mutedc">{d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-16 text-center sm:px-6 lg:py-28 lg:px-8">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-crimson">Founder's Note</p>
          <blockquote className="mt-8 font-serif text-3xl font-light italic leading-snug sm:text-4xl" data-testid="about-founder-quote">
            "A co-ord is the quickest confidence there is — dress in ten seconds, look styled all day. That ease is what we sew into every set."
          </blockquote>
          <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.3em] text-mutedc">Team Just Kords</p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
        <Reveal>
          <div className="flex flex-col items-start justify-between gap-8 bg-crimson p-10 text-white sm:p-14 md:flex-row md:items-center">
            <div>
              <h2 className="font-serif text-3xl font-light sm:text-4xl">Find your mood.</h2>
              <p className="mt-2 text-sm text-white/75">Resort linen to after-dark satin — the collection is waiting.</p>
            </div>
            <Link to="/shop" data-testid="about-shop-cta" className="group inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-semibold text-crimson transition-colors duration-200 hover:bg-ink hover:text-white">
              Shop the collection <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
